import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useListAgenticWmThreadsQuery,
  useGetAgenticWmThreadQuery,
  useOpenAgenticWmThreadMutation,
  useRenameAgenticWmThreadMutation,
  useArchiveAgenticWmThreadMutation,
  useSetAgenticWmFeedbackMutation,
  useSearchCrmClientsQuery,
  useGetCrmClientQuery,
} from '../../app/api';
import { selectAccessToken } from '../../features/auth/authSlice';
import { useIsMobile } from '../utils';

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
  bubble:    '#e8f1f8',
};

const fmtWhen = (d) => (d ? new Date(`${String(d).replace(' ', 'T')}Z`).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '');

// Starters for an empty conversation - the kinds of work the instructions
// describe, phrased for one client.
const STARTERS = (first) => [
  `Prep me for ${first}'s annual review - what should I look at first?`,
  'Which fee tier applies here, and what would it cost per year?',
  'Walk me through Roth conversion considerations for this household.',
  'Map this household onto the Bucket Plan - what do we still need to know?',
];

const MARKDOWN = {
  table: (props) => <table className="table table-sm table-bordered" style={{ fontSize: '0.84rem' }} {...props} />,
  a: ({ href, children }) => (
    <a href={href} target={/^https?:\/\//i.test(href || '') ? '_blank' : undefined} rel="noopener noreferrer"
       style={{ color: work.tealBlue }}>{children}</a>
  ),
};

function Markdown({ children }) {
  return (
    <div className="agentic-wm-md" style={{ fontSize: '0.9rem', lineHeight: 1.55, color: work.ink, overflowX: 'auto' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN}>{children || ''}</ReactMarkdown>
    </div>
  );
}
Markdown.propTypes = { children: PropTypes.string };

function Reasoning({ text, open: startOpen = false }) {
  if (!text) return null;
  return (
    <details open={startOpen} style={{ marginBottom: '0.5rem' }}>
      <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: work.muted }}>Reasoning</summary>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: work.muted, borderLeft: `2px solid ${work.hairline}`, paddingLeft: '0.6rem', marginTop: 4 }}>
        {text}
      </div>
    </details>
  );
}
Reasoning.propTypes = { text: PropTypes.string, open: PropTypes.bool };

const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_e) { return url; } };

/** The web searches behind a reply: live progress while it streams, then the
 * queries and every result they returned. AWS's terms for the search index
 * require the source links to be shown wherever a result is used. */
function Searches({ searches, streaming }) {
  if (!searches?.length) return null;
  const results = searches.reduce((n, s) => n + (s.results?.length || s.count || 0), 0);
  if (streaming) {
    return (
      <div style={{ fontSize: '0.78rem', color: work.muted, marginBottom: '0.5rem' }}>
        {searches.map((s, i) => (
          <div key={i}>
            <i className="bi bi-search me-1" />
            {s.done ? `Searched "${s.query}" - ${s.count} result${s.count === 1 ? '' : 's'}` : `Searching "${s.query}"…`}
          </div>
        ))}
      </div>
    );
  }
  return (
    <details style={{ marginTop: '0.6rem', borderTop: `1px solid ${work.hairline}`, paddingTop: '0.4rem' }}>
      <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: work.muted }}>
        <i className="bi bi-search me-1" />
        Searched the web {searches.length} time{searches.length === 1 ? '' : 's'} - {results} source{results === 1 ? '' : 's'}
      </summary>
      {searches.map((s, i) => (
        <div key={i} style={{ marginTop: 6 }}>
          <div style={{ fontSize: '0.76rem', color: work.ink, fontWeight: 600 }}>&quot;{s.query}&quot;{s.error ? ` - ${s.error}` : ''}</div>
          <ul style={{ margin: '2px 0 0', paddingLeft: '1.1rem' }}>
            {(s.results || []).map((r, j) => (
              <li key={j} style={{ fontSize: '0.76rem' }}>
                <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: work.tealBlue }}>{r.title || r.url}</a>
                <span style={{ color: work.muted }}> - {hostOf(r.url)}{r.published && r.published !== 'unknown' ? `, ${r.published}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </details>
  );
}
Searches.propTypes = { searches: PropTypes.array, streaming: PropTypes.bool };

/** Thumbs up / down on one reply, with an optional comment. Read-only (no
 * buttons) when `onSave` is absent - the Super Admin review shows the tester's
 * rating without letting the reviewer change it. */
function Feedback({ feedback, onSave }) {
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(feedback?.rating || 0);
  const [comment, setComment] = useState(feedback?.comment || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRating(feedback?.rating || 0); setComment(feedback?.comment || ''); }, [feedback]);

  if (!onSave) {
    if (!feedback) return null;
    return (
      <div style={{ marginTop: 8, fontSize: '0.78rem', color: feedback.rating > 0 ? '#1a7f4e' : '#b42318' }}>
        <i className={`bi bi-hand-thumbs-${feedback.rating > 0 ? 'up' : 'down'}-fill me-1`} />
        {feedback.rating > 0 ? 'Helpful' : 'Not helpful'}{feedback.comment ? ` - "${feedback.comment}"` : ''}
      </div>
    );
  }
  const save = async (next, nextComment) => {
    setSaving(true);
    await onSave(next, nextComment);
    setSaving(false);
  };
  const pick = (value) => {
    const next = rating === value ? 0 : value;  // clicking the chosen thumb again clears it
    setRating(next);
    setEditing(next !== 0);
    save(next, next ? comment : '');
  };
  const btn = (value, icon, label) => (
    <button type="button" className="btn btn-link btn-sm p-0" title={label} disabled={saving} onClick={() => pick(value)}
            style={{ color: rating === value ? (value > 0 ? '#1a7f4e' : '#b42318') : work.muted }}>
      <i className={`bi bi-hand-thumbs-${icon}${rating === value ? '-fill' : ''}`} />
    </button>
  );
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.76rem', color: work.muted }}>
        <span>Was this helpful?</span>
        {btn(1, 'up', 'Helpful')}
        {btn(-1, 'down', 'Not helpful')}
        {rating !== 0 && !editing && (
          <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '0.76rem', color: work.tealBlue }} onClick={() => setEditing(true)}>
            {comment ? 'Edit comment' : 'Add a comment'}
          </button>
        )}
        {rating !== 0 && !editing && comment && <span style={{ fontStyle: 'italic' }}>&quot;{comment}&quot;</span>}
      </div>
      {editing && (
        <div style={{ display: 'flex', gap: '0.4rem', marginTop: 6 }}>
          <input className="form-control form-control-sm" maxLength={2000} value={comment} autoFocus
                 placeholder={rating > 0 ? 'What was useful? (optional)' : 'What was wrong or missing? (optional)'}
                 onChange={(e) => setComment(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(false); save(rating, comment); } }} />
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={saving}
                  onClick={() => { setEditing(false); save(rating, comment); }}>Save</button>
        </div>
      )}
    </div>
  );
}
Feedback.propTypes = { feedback: PropTypes.object, onSave: PropTypes.func };

export function Message({ m, onFeedback }) {
  if (m.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0.75rem 0' }}>
        <div style={{ maxWidth: '80%', background: work.bubble, borderRadius: 10, padding: '0.55rem 0.8rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: work.ink }}>
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div style={{ margin: '0.75rem 0', background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 10, padding: '0.75rem 0.95rem' }}>
      <Reasoning text={m.reasoning} open={m.streaming && !m.text} />
      {m.streaming && <Searches searches={m.searches} streaming />}
      {m.text ? <Markdown>{m.text}</Markdown> : m.streaming && (
        <div style={{ color: work.muted, fontSize: '0.85rem' }}>
          <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
          {m.reasoning ? 'Thinking it through…' : 'Reading the client record and project files…'}
        </div>
      )}
      {m.status === 'error' && (
        <div style={{ color: '#b42318', fontSize: '0.82rem', marginTop: m.text ? 6 : 0 }}>
          {m.error && !m.error.includes(':') ? m.error : 'This reply did not finish. Ask again to retry.'}
        </div>
      )}
      {m.stop_reason === 'max_tokens' && (
        <div style={{ color: work.muted, fontSize: '0.78rem', marginTop: 6 }}>This reply hit the length limit - ask it to continue.</div>
      )}
      {!m.streaming && <Searches searches={m.searches} />}
      {!m.streaming && m.status === 'complete' && (onFeedback || m.feedback) && (
        <Feedback feedback={m.feedback} onSave={onFeedback && ((rating, comment) => onFeedback(m.id, rating, comment))} />
      )}
    </div>
  );
}
Message.propTypes = { m: PropTypes.object.isRequired, onFeedback: PropTypes.func };

/** Thread list, grouped by client, newest activity first. */
function ThreadList({ threads, activeId, clientFilter }) {
  const groups = useMemo(() => {
    const byClient = new Map();
    (threads || []).forEach((t) => {
      if (!byClient.has(t.crm_client_id)) byClient.set(t.crm_client_id, { name: t.client_name, items: [] });
      byClient.get(t.crm_client_id).items.push(t);
    });
    return [...byClient.entries()];
  }, [threads]);

  if (!threads?.length) {
    return <div style={{ color: work.muted, fontSize: '0.85rem', padding: '0.5rem 0.25rem' }}>
      {clientFilter ? 'No conversations for this client yet.' : 'No conversations yet. Pick a client to start one.'}
    </div>;
  }
  return groups.map(([clientId, g]) => (
    <div key={clientId} style={{ marginBottom: '0.9rem' }}>
      {!clientFilter && (
        <Link to={`/work/agentic-wm?client=${clientId}`} style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: work.muted, textDecoration: 'none', margin: '0 0 0.25rem 0.25rem' }}>
          {g.name}
        </Link>
      )}
      {g.items.map((t) => (
        <Link key={t.id} to={`/work/agentic-wm/${t.id}`} style={{
          display: 'block', padding: '0.45rem 0.6rem', borderRadius: 6, textDecoration: 'none',
          background: t.id === activeId ? work.bubble : 'transparent',
          borderLeft: `3px solid ${t.id === activeId ? work.goldenrod : 'transparent'}`,
        }}>
          <div style={{ fontSize: '0.86rem', color: work.ink, fontWeight: t.id === activeId ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
          <div style={{ fontSize: '0.72rem', color: work.muted }}>
            {fmtWhen(t.updated)}{t.replies ? ` · ${t.replies} ${t.replies === 1 ? 'reply' : 'replies'}` : ''}
          </div>
        </Link>
      ))}
    </div>
  ));
}
ThreadList.propTypes = { threads: PropTypes.array, activeId: PropTypes.number, clientFilter: PropTypes.number };

/** Find a CRM client to open a conversation for. Same scoped search as Work -> CRM. */
function ClientPicker({ onPick, busy }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const { data, isFetching } = useSearchCrmClientsQuery(
    { q: debounced, status_group: 'all', limit: 8 }, { skip: debounced.length < 2 });
  return (
    <div>
      <input className="form-control form-control-sm" placeholder="Find a client by name, email or city" value={q}
             onChange={(e) => setQ(e.target.value)} disabled={busy} />
      {debounced.length >= 2 && (
        <div style={{ border: `1px solid ${work.hairline}`, borderRadius: 6, marginTop: 4, background: work.surface }}>
          {isFetching && <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: work.muted }}>Searching…</div>}
          {!isFetching && !data?.clients?.length && (
            <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: work.muted }}>No clients match in the offices you can see.</div>
          )}
          {!isFetching && data?.clients?.map((c) => (
            <button key={c.id} type="button" disabled={busy} onClick={() => onPick(c.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderTop: `1px solid ${work.hairline}`, background: 'transparent', padding: '0.4rem 0.6rem' }}>
              <div style={{ fontSize: '0.85rem', color: work.ink, fontWeight: 600 }}>{c.display_name}</div>
              <div style={{ fontSize: '0.72rem', color: work.muted }}>
                {[c.source_name, [c.city, c.state].filter(Boolean).join(', '), c.status].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
ClientPicker.propTypes = { onPick: PropTypes.func.isRequired, busy: PropTypes.bool };

/** One conversation: history, the streaming reply, and the composer. */
function Conversation({ threadId, onChanged }) {
  const navigate = useNavigate();
  const token = useSelector(selectAccessToken);
  const { data: thread, isLoading, isError, refetch } = useGetAgenticWmThreadQuery(threadId);
  const [rename] = useRenameAgenticWmThreadMutation();
  const [archive] = useArchiveAgenticWmThreadMutation();
  const [setFeedback] = useSetAgenticWmFeedbackMutation();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(null); // { question, reply } while streaming
  const [sendError, setSendError] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { setPending(null); setSendError(null); setDraft(''); }, [threadId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [thread?.messages?.length, pending?.reply?.text?.length]);

  if (isLoading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="spinner-border" style={{ color: work.tealBlue }} /></div>;
  if (isError || !thread) {
    return <div style={{ padding: '2rem', color: work.muted }}>This conversation was not found, or its client is outside the offices you can see.</div>;
  }

  const firstName = (thread.client_name || '').split(' ')[0] || 'this client';
  const streaming = Boolean(pending);

  const send = async (text) => {
    const question = (text ?? draft).trim();
    if (!question || streaming) return;
    setSendError(null);
    setDraft('');
    setPending({ question, reply: { role: 'assistant', text: '', reasoning: '', searches: [], streaming: true } });
    try {
      const res = await fetch(`/api/agentic-wm/threads/${threadId}/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: question }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setPending(null);
        setDraft(question);
        setSendError(typeof body.detail === 'string' ? body.detail : 'Could not send - try again.');
        return;
      }
      // NDJSON: one event per line - thinking/text deltas, then done or error.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          const ev = JSON.parse(line);
          if (ev.type === 'start') {
            setPending((p) => p && ({ ...p, ids: [ev.question_id, ev.reply_id] }));
          } else if (ev.type === 'search') {
            setPending((p) => p && ({ ...p, reply: { ...p.reply, searches: [...p.reply.searches, { query: ev.query }] } }));
          } else if (ev.type === 'search_done') {
            setPending((p) => {
              if (!p) return p;
              const searches = [...p.reply.searches];
              const i = searches.findIndex((s) => s.query === ev.query && !s.done);
              if (i >= 0) searches[i] = { ...searches[i], done: true, count: ev.count };
              return { ...p, reply: { ...p.reply, searches } };
            });
          } else if (ev.type === 'text' || ev.type === 'thinking') {
            const key = ev.type === 'text' ? 'text' : 'reasoning';
            setPending((p) => p && ({ ...p, reply: { ...p.reply, [key]: p.reply[key] + ev.text } }));
          } else if (ev.type === 'error') {
            setSendError(ev.detail);
          }
        }
      }
    } catch (e) {
      setSendError('The connection dropped. The answer may still finish - reopen this conversation in a minute.');
    }
    await refetch();
    setPending(null);
    onChanged();
  };

  const saveTitle = async () => {
    const title = (editingTitle || '').trim();
    setEditingTitle(null);
    if (title && title !== thread.title) {
      await rename({ id: threadId, title });
      refetch();
      onChanged();
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove this conversation from your list?')) return;
    await archive(threadId);
    onChanged();
    navigate(`/work/agentic-wm?client=${thread.client_id}`);
  };

  // A reply still being written elsewhere (another tab, or this one before a
  // reload) shows as in progress until it lands.
  const stillWriting = !streaming && thread.messages.some((m) => m.status === 'streaming');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.6rem', borderBottom: `1px solid ${work.hairline}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingTitle !== null ? (
            <input className="form-control form-control-sm" autoFocus value={editingTitle} maxLength={200}
                   onChange={(e) => setEditingTitle(e.target.value)} onBlur={saveTitle}
                   onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(null); }} />
          ) : (
            <div style={{ fontWeight: 700, color: work.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {thread.title}
              <button type="button" className="btn btn-link btn-sm p-0 ms-2" title="Rename" onClick={() => setEditingTitle(thread.title)}>
                <i className="bi bi-pencil" style={{ color: work.muted }} />
              </button>
            </div>
          )}
          <div style={{ fontSize: '0.76rem', color: work.muted }}>
            <Link to={`/work/crm/${thread.client_id}`} style={{ color: work.tealBlue, textDecoration: 'none' }}>{thread.client_name}</Link>
            {thread.opened_snapshot && ` · CRM snapshot from ${thread.opened_snapshot}`}
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" title="Remove from my list" onClick={remove}>
          <i className="bi bi-archive" />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0.25rem 0.1rem' }}>
        {!thread.messages.length && !pending && (
          <div style={{ padding: '1.25rem 0.25rem', color: work.muted, fontSize: '0.88rem' }}>
            <p style={{ marginBottom: '0.6rem' }}>Ask anything about {thread.client_name}. The advisor has their CRM record, the Bucket Plan and Tax Management Journey materials, the Valor models and the fee schedule.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {STARTERS(firstName).map((s) => (
                <button key={s} type="button" className="btn btn-outline-secondary btn-sm" style={{ textAlign: 'left' }} onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {/* While a reply streams, the stored copies of this turn (once a
            refetch has them) give way to the live ones, so nothing shows twice. */}
        {thread.messages.filter((m) => !pending?.ids?.includes(m.id)).map((m) => (
          <Message key={m.id} m={m.status === 'streaming' ? { ...m, streaming: true } : m}
                   onFeedback={async (id, rating, comment) => { await setFeedback({ id, rating, comment }); refetch(); }} />
        ))}
        {pending && (
          <>
            <Message m={{ role: 'user', text: pending.question }} />
            <Message m={pending.reply} />
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {sendError && <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '0.84rem' }}>{sendError}</div>}
      <div style={{ borderTop: `1px solid ${work.hairline}`, paddingTop: '0.6rem' }}>
        <textarea className="form-control" rows={3} value={draft} disabled={streaming || stillWriting}
                  placeholder={stillWriting ? 'Still answering - refresh in a moment.' : `Ask about ${firstName}… (Enter to send, Shift+Enter for a new line)`}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 6, gap: '0.5rem' }}>
          <span style={{ fontSize: '0.72rem', color: work.muted, flex: 1 }}>
            AI-generated analysis for advisor use - verify before it reaches a client. Web figures are cited; anything it could not check is marked unverified. Beta conversations may be reviewed by Prosperity for quality.
          </span>
          <button type="button" className="btn btn-sm text-white" style={{ background: work.tealBlue }}
                  disabled={streaming || stillWriting || !draft.trim()} onClick={() => send()}>
            {streaming ? <span className="spinner-border spinner-border-sm" /> : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
Conversation.propTypes = { threadId: PropTypes.number.isRequired, onChanged: PropTypes.func.isRequired };

/**
 * Work -> Agentic WM. The Prosperity Agentic Wealth Advisor, one conversation
 * thread per client, kept so advisors can come back to it. Opened from here or
 * from a client's page in Work -> CRM (?client=<crm id>). Threads are private
 * to the advisor who opened them.
 */
export default function AgenticWm() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { threadId: threadParam } = useParams();
  const [searchParams] = useSearchParams();
  const threadId = threadParam ? Number(threadParam) : null;
  const clientFilter = !threadId && searchParams.get('client') ? Number(searchParams.get('client')) : null;

  const { data: list, refetch: refetchList } = useListAgenticWmThreadsQuery(clientFilter ? { client_id: clientFilter } : {});
  const { data: filterClient } = useGetCrmClientQuery({ id: clientFilter }, { skip: !clientFilter });
  const [open, { isLoading: opening }] = useOpenAgenticWmThreadMutation();
  const [openError, setOpenError] = useState(null);

  const startFor = async (clientId) => {
    setOpenError(null);
    const res = await open({ client_id: clientId });
    if (res.error) {
      setOpenError(res.error.data?.detail || 'Could not start a conversation.');
      return;
    }
    refetchList();
    navigate(`/work/agentic-wm/${res.data.id}`);
  };

  const sidebar = (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 8, padding: '0.75rem', overflowY: 'auto', ...(isMobile ? {} : { height: '100%' }) }}>
      {clientFilter ? (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted }}>Client</div>
          <div style={{ fontWeight: 700, color: work.ink }}>
            {filterClient ? <Link to={`/work/crm/${clientFilter}`} style={{ color: work.ink }}>{filterClient.display_name}</Link> : '…'}
          </div>
          <button type="button" className="btn btn-sm text-white w-100 mt-2" style={{ background: work.tealBlue }}
                  disabled={opening || !filterClient} onClick={() => startFor(clientFilter)}>
            <i className="bi bi-plus-lg me-1" />New conversation
          </button>
          <Link to="/work/agentic-wm" style={{ display: 'inline-block', fontSize: '0.78rem', color: work.tealBlue, marginTop: 6 }}>All my conversations</Link>
        </div>
      ) : (
        <div style={{ marginBottom: '0.9rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: work.muted, marginBottom: 4 }}>New conversation</div>
          <ClientPicker onPick={startFor} busy={opening} />
        </div>
      )}
      {openError && <div className="alert alert-warning py-1 px-2" style={{ fontSize: '0.8rem' }}>{openError}</div>}
      <ThreadList threads={list?.threads} activeId={threadId} clientFilter={clientFilter} />
    </div>
  );

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{ background: work.deepBlue, borderBottom: `3px solid ${work.goldenrod}`, borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1rem', color: 'white' }}>
        <h4 style={{ margin: 0, fontWeight: 700 }}><i className="bi bi-stars me-2" style={{ color: work.goldenrod }} />Agentic WM</h4>
        <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)' }}>
          Your analytical team for The Bucket Plan® process - Discover, Design, Deliver and Dedicate - across the Five Pillars. One conversation per client question, kept here for you to come back to.
        </div>
      </div>

      <div style={{
        display: 'grid', gap: '1rem',
        gridTemplateColumns: isMobile ? '1fr' : '300px minmax(0, 1fr)',
        ...(isMobile ? {} : { height: 'calc(100vh - 210px)', minHeight: 480 }),
      }}>
        {(!isMobile || !threadId) && sidebar}
        <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 8, padding: '0.9rem 1rem', minHeight: 0, ...(isMobile ? { minHeight: '70vh' } : {}) }}>
          {threadId ? (
            <>
              {isMobile && <Link to="/work/agentic-wm" style={{ fontSize: '0.82rem', color: work.tealBlue }}>&larr; Conversations</Link>}
              <Conversation threadId={threadId} onChanged={refetchList} />
            </>
          ) : (
            <div style={{ color: work.muted, fontSize: '0.9rem', padding: '1rem 0.5rem', maxWidth: 640 }}>
              <p>Pick a client to start a conversation, or open one from the list. You can also start one from a client&apos;s page in Work &rarr; CRM.</p>
              <p style={{ marginBottom: '0.4rem' }}>Each conversation starts with that client&apos;s CRM record and the firm&apos;s preset context: the Agentic Wealth Advisor instructions, Bucket Plan and Tax Management Journey materials, Valor models and the fee schedule.</p>
              <p style={{ marginBottom: 0 }}>Conversations are private to you, though Prosperity may review them for quality and supervision while Agentic WM is in beta. It searches the web to verify current figures (tax law, Medicare, carrier rates) and cites its sources. Document upload and deck building are coming - until then, use the Prosperity Agentic Wealth Advisor Claude project for those.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
